import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Bar, BarChart, Cell } from "recharts";
import { ArrowLeft, TrendingUp, TrendingDown, Sparkles } from "lucide-react";
import { useWorkspace } from "@/context/WorkspaceContext";
import { tokenTrend, topConsumers } from "@/data/mockData";

const META = {
  "ai-assets": { title: "AI Assets", desc: "Every AI system discovered across your enterprise — endpoints, agents, assistants.", unit: "" },
  "active-agents": { title: "Active Agents", desc: "Agents actively executing workflows during the selected window.", unit: "" },
  "critical-risks": { title: "Critical Risks", desc: "Open critical-severity risks across all governed AI assets.", unit: "" },
  "human-reviews": { title: "Human Reviews", desc: "Cases queued for human reviewers — SLA window 15 min.", unit: "" },
  "policy-violations": { title: "Policy Violations", desc: "Detected breaches of policy rules across runtime evaluations.", unit: "" },
  "runtime-events": { title: "Runtime Events", desc: "All agent runtime events streamed through AI-ECO in the last 7 days.", unit: "" },
};

const CTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-[#1A2238]/95 px-3 py-2 text-xs backdrop-blur-md">
      <div className="text-slate-400">{label}</div>
      <div className="font-mono text-slate-200">{payload[0].name}: <span style={{ color: payload[0].color }}>{Number(payload[0].value).toLocaleString()}</span></div>
    </div>
  );
};

export default function KpiExplorer() {
  const { kpiKey } = useParams();
  const navigate = useNavigate();
  const { data } = useWorkspace();
  const kpi = data.kpis.find((k) => k.key === kpiKey);
  const meta = META[kpiKey] || { title: kpi?.label || "Metric", desc: "" };
  if (!kpi) {
    return (
      <div className="mx-auto max-w-[1480px] px-10 py-12 text-slate-300">
        <Link to="/" className="text-[#5B8DEF]">← Command Center</Link>
        <div className="mt-3 text-sm text-slate-400">Metric not found.</div>
      </div>
    );
  }

  const isRisk = kpi.label.toLowerCase().includes("risk") || kpi.label.toLowerCase().includes("violation");
  const up = kpi.trend === "up";
  const positive = isRisk ? !up : up;
  const color = positive ? "#22C55E" : isRisk ? "#EF4444" : "#5B8DEF";
  const TrendIcon = up ? TrendingUp : TrendingDown;

  // Build series from sparkline (expand to 14 days by repeating + perturbing)
  const series = (() => {
    const base = kpi.spark || [];
    const out = [];
    for (let i = 0; i < 14; i++) {
      const v = base[i % base.length] * (0.9 + (i * 7 % 5) / 50);
      out.push({ day: `D${i + 1}`, value: Math.round(v) });
    }
    out[out.length - 1].value = kpi.value;
    return out;
  })();

  const breakdown = [
    { name: "Production", value: Math.round(kpi.value * 0.62), color: "#5B8DEF" },
    { name: "Staging", value: Math.round(kpi.value * 0.22), color: "#22C55E" },
    { name: "Dev", value: Math.round(kpi.value * 0.16), color: "#F59E0B" },
  ];

  return (
    <div className="mx-auto max-w-[1480px] px-6 py-8 lg:px-10 lg:py-10" data-testid={`page-explore-${kpi.key}`}>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <button onClick={() => navigate("/")} data-testid="back-to-command" className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-slate-200">
          <ArrowLeft size={13} /> Command Center
        </button>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-400">
              <Sparkles size={12} className="text-[#5B8DEF]" /> Metric explorer · {data.name}
            </div>
            <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-white">{meta.title}</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">{meta.desc}</p>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-5xl font-light text-white">{Number(kpi.value).toLocaleString()}</span>
            <span className="flex items-center gap-1 font-mono text-[12px]" style={{ color }}>
              <TrendIcon size={12} />
              {kpi.delta}
            </span>
          </div>
        </div>
      </motion.div>

      <div className="mt-7 grid grid-cols-12 gap-6">
        <div className="card-eco col-span-12 p-6 lg:col-span-8">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">14-day trend</div>
          <div className="mt-3 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="kfill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="day" stroke="#475569" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <YAxis stroke="#475569" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip content={<CTooltip />} />
                <Area type="monotone" dataKey="value" name={meta.title} stroke={color} strokeWidth={1.8} fill="url(#kfill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-eco col-span-12 p-6 lg:col-span-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Environment breakdown</div>
          <div className="mt-3 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={breakdown} layout="vertical" margin={{ left: 0, right: 8, top: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" horizontal={false} />
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" stroke="#94a3b8" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
                <Tooltip content={<CTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18}>
                  {breakdown.map((c, i) => (<Cell key={i} fill={c.color} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-eco col-span-12 p-6">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Recent changes</div>
            <Link to="/audit" className="text-[11px] text-[#5B8DEF]">View full audit →</Link>
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                <th className="px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Δ</th>
                <th className="px-3 py-2 font-medium">Note</th>
              </tr>
            </thead>
            <tbody className="text-[12px]">
              {[
                { t: "09:01", s: "Decision Agent", d: "+2", n: "Loan approval workflow" },
                { t: "09:14", s: "Policy Engine", d: "+1", n: "Violation: exceeds 3× income" },
                { t: "09:22", s: "Credit Agent", d: "+1", n: "Blocked: marital_status" },
                { t: "10:02", s: "Support Bot", d: "+1", n: "Auto-resolved query" },
              ].map((r, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-3 py-2 font-mono text-slate-300">{r.t}</td>
                  <td className="px-3 py-2 text-slate-200">{r.s}</td>
                  <td className="px-3 py-2 font-mono" style={{ color }}>{r.d}</td>
                  <td className="px-3 py-2 text-slate-400">{r.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
